/**
 * WorkspaceHandler: ScratchIR ↔ VM操作 の変換レイヤー
 *
 * Scratch VMの操作をScratchIR経由で行うためのハンドラ。
 * iframe-communication.js から呼び出される。
 */

import { ScratchIRDecompiler, ScratchIRCompiler } from 'scratch-ir';

class WorkspaceHandler {
    constructor () {
        this.vm = null;
        this.decompiler = new ScratchIRDecompiler();
        this.compiler = new ScratchIRCompiler();
    }

    setVM (vm) {
        this.vm = vm;
    }

    // ===== 観測系 =====

    /**
     * ワークスペース全体のIRスナップショットを取得
     */
    getSnapshot () {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const projectJSON = this._getProjectJSON(vm);
        return this.decompiler.decompile(projectJSON);
    }

    /**
     * スプライト一覧を取得（コスチューム情報付き）
     */
    getSpriteList () {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const sprites = [];
        for (const target of vm.runtime.targets) {
            if (target.isStage) continue;
            sprites.push({
                name: target.sprite.name,
                costumeCount: target.sprite.costumes.length,
                costumes: target.sprite.costumes.map(c => ({
                    name: c.name,
                    assetId: c.assetId,
                    md5ext: c.md5ext || `${c.assetId}.${c.dataFormat}`,
                    dataFormat: c.dataFormat
                })),
                soundCount: target.sprite.sounds.length,
                scriptCount: Object.values(target.blocks._blocks)
                    .filter(b => b.topLevel && !b.shadow).length,
                position: {x: target.x, y: target.y},
                visible: target.visible,
                size: target.size,
                direction: target.direction
            });
        }
        return sprites;
    }

    /**
     * スプライト詳細をIRで取得
     */
    getSpriteDetail (spriteName) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const ir = this.getSnapshot();
        const sprite = ir.sprites.find(s => s.name === spriteName);
        if (!sprite) {
            const available = ir.sprites.map(s => s.name);
            throw new Error(
                `Sprite "${spriteName}" not found. Available sprites: [${available.join(', ')}]`
            );
        }
        return sprite;
    }

    /**
     * 実行状態を取得
     */
    getExecutionState () {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        return {
            running: vm.runtime.threads.length > 0,
            turbo: vm.runtime.turboMode || false
        };
    }

    // ===== 制御系 =====

    /**
     * IR全体を適用（プロジェクト全置換）
     */
    async applyIR (ir) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const backup = this._getProjectJSON(vm);
        let projectJSON;
        try {
            const compiler = new ScratchIRCompiler();
            projectJSON = compiler.compile(ir);
        } catch (compileError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] applyIR compile failed:', compileError);
            throw new Error(`IR compilation failed: ${compileError.message}`);
        }

        try {
            await vm.loadProject(projectJSON);
        } catch (loadError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] applyIR loadProject failed:', loadError);
            await this._restoreBackup(vm, backup);
            throw new Error(`Project loading failed after applyIR: ${loadError.message}`);
        }
    }

    /**
     * スプライトにスクリプトを追加
     *
     * 現在のプロジェクトをIRに変換 → スクリプト追加 → 再コンパイル → ロード
     * 失敗時は自動的にバックアップから復元する
     */
    async addScript (spriteName, scriptIR) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        // scriptIR バリデーション
        if (!scriptIR || typeof scriptIR !== 'object') {
            throw new Error('Invalid script: scriptIR must be an object');
        }
        if (!scriptIR.trigger || !scriptIR.trigger.type) {
            throw new Error(
                `Invalid script: must have trigger with type. Got: ${JSON.stringify(scriptIR.trigger)}`
            );
        }
        if (scriptIR.body !== undefined && !Array.isArray(scriptIR.body)) {
            throw new Error('Invalid script: body must be an array');
        }
        // body が未定義なら空配列にする
        if (!scriptIR.body) {
            scriptIR.body = [];
        }

        const backup = this._getProjectJSON(vm);
        const ir = this.getSnapshot();

        if (spriteName === 'Stage' || spriteName === '_stage_') {
            ir.stage.scripts.push(scriptIR);
        } else {
            const sprite = ir.sprites.find(s => s.name === spriteName);
            if (!sprite) {
                const available = ir.sprites.map(s => s.name);
                throw new Error(
                    `Sprite "${spriteName}" not found. Available sprites: [${available.join(', ')}]`
                );
            }
            sprite.scripts.push(scriptIR);
        }

        // eslint-disable-next-line no-console
        console.log('[WorkspaceHandler] addScript: sprite=%s, trigger=%s, bodyLen=%d',
            spriteName,
            scriptIR?.trigger?.type || 'unknown',
            scriptIR?.body?.length || 0
        );

        // コンパイルとロードを分けてエラーを特定しやすくする
        let projectJSON;
        try {
            const compiler = new ScratchIRCompiler();
            projectJSON = compiler.compile(ir);
        } catch (compileError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] addScript compile failed:', compileError);
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] Failed scriptIR:', JSON.stringify(scriptIR, null, 2));
            throw new Error(`IR compilation failed: ${compileError.message}`);
        }

        try {
            await vm.loadProject(projectJSON);
        } catch (loadError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] addScript loadProject failed:', loadError);
            await this._restoreBackup(vm, backup);
            throw new Error(`Project loading failed after addScript: ${loadError.message}`);
        }
    }

    /**
     * スクリプトを削除
     */
    async removeScript (spriteName, scriptIndex) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const backup = this._getProjectJSON(vm);
        const ir = this.getSnapshot();

        if (spriteName === 'Stage' || spriteName === '_stage_') {
            if (scriptIndex < 0 || scriptIndex >= ir.stage.scripts.length) {
                throw new Error(`Script index ${scriptIndex} out of range`);
            }
            ir.stage.scripts.splice(scriptIndex, 1);
        } else {
            const sprite = ir.sprites.find(s => s.name === spriteName);
            if (!sprite) {
                const available = ir.sprites.map(s => s.name);
                throw new Error(
                    `Sprite "${spriteName}" not found. Available sprites: [${available.join(', ')}]`
                );
            }
            if (scriptIndex < 0 || scriptIndex >= sprite.scripts.length) {
                throw new Error(`Script index ${scriptIndex} out of range`);
            }
            sprite.scripts.splice(scriptIndex, 1);
        }

        let projectJSON;
        try {
            const compiler = new ScratchIRCompiler();
            projectJSON = compiler.compile(ir);
        } catch (compileError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] removeScript compile failed:', compileError);
            throw new Error(`IR compilation failed: ${compileError.message}`);
        }

        try {
            await vm.loadProject(projectJSON);
        } catch (loadError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] removeScript loadProject failed:', loadError);
            await this._restoreBackup(vm, backup);
            throw new Error(`Project loading failed after removeScript: ${loadError.message}`);
        }
    }

    /**
     * スプライトを追加
     * コスチュームが未指定・無効な場合は既存スプライトからコスチュームをコピーする
     */
    async addSprite (spriteIR) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const backup = this._getProjectJSON(vm);
        const ir = this.getSnapshot();

        // デフォルト値を埋める
        if (!spriteIR.position) spriteIR.position = {x: 0, y: 0};
        if (spriteIR.direction === undefined) spriteIR.direction = 90;
        if (spriteIR.size === undefined) spriteIR.size = 100;
        if (spriteIR.visible === undefined) spriteIR.visible = true;
        if (!spriteIR.rotationStyle) spriteIR.rotationStyle = 'all-around';
        if (!spriteIR.variables) spriteIR.variables = [];
        if (!spriteIR.lists) spriteIR.lists = [];
        if (!spriteIR.broadcasts) spriteIR.broadcasts = [];
        if (!spriteIR.scripts) spriteIR.scripts = [];
        if (!spriteIR.sounds) spriteIR.sounds = [];
        if (spriteIR.currentCostume === undefined) spriteIR.currentCostume = 0;
        if (spriteIR.volume === undefined) spriteIR.volume = 100;
        if (spriteIR.draggable === undefined) spriteIR.draggable = false;

        // コスチュームの検証と自動補完
        spriteIR.costumes = this._ensureValidCostumes(spriteIR.costumes, ir, vm);

        // 同名スプライト回避
        const existingNames = new Set(ir.sprites.map(s => s.name));
        if (existingNames.has(spriteIR.name)) {
            let counter = 2;
            const baseName = spriteIR.name;
            while (existingNames.has(`${baseName}${counter}`)) counter++;
            spriteIR.name = `${baseName}${counter}`;
        }

        ir.sprites.push(spriteIR);

        let projectJSON;
        try {
            const compiler = new ScratchIRCompiler();
            projectJSON = compiler.compile(ir);
        } catch (compileError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] addSprite compile failed:', compileError);
            throw new Error(`IR compilation failed: ${compileError.message}`);
        }

        try {
            await vm.loadProject(projectJSON);
        } catch (loadError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] addSprite loadProject failed:', loadError);
            await this._restoreBackup(vm, backup);
            throw new Error(`Project loading failed after addSprite: ${loadError.message}`);
        }
    }

    /**
     * スプライトを削除
     */
    async deleteSprite (spriteName) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        const backup = this._getProjectJSON(vm);
        const ir = this.getSnapshot();
        const idx = ir.sprites.findIndex(s => s.name === spriteName);
        if (idx === -1) {
            const available = ir.sprites.map(s => s.name);
            throw new Error(
                `Sprite "${spriteName}" not found. Available sprites: [${available.join(', ')}]`
            );
        }
        ir.sprites.splice(idx, 1);

        let projectJSON;
        try {
            const compiler = new ScratchIRCompiler();
            projectJSON = compiler.compile(ir);
        } catch (compileError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] deleteSprite compile failed:', compileError);
            throw new Error(`IR compilation failed: ${compileError.message}`);
        }

        try {
            await vm.loadProject(projectJSON);
        } catch (loadError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] deleteSprite loadProject failed:', loadError);
            await this._restoreBackup(vm, backup);
            throw new Error(`Project loading failed after deleteSprite: ${loadError.message}`);
        }
    }

    /**
     * 変数を設定
     */
    setVariable (name, value, spriteName) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        // ターゲットを特定
        let target;
        if (!spriteName || spriteName === 'Stage' || spriteName === '_stage_') {
            target = vm.runtime.getTargetForStage();
        } else {
            target = vm.runtime.getSpriteTargetByName(spriteName);
        }
        if (!target) throw new Error(`Target "${spriteName || 'Stage'}" not found`);

        // 変数を探して値を設定
        const variables = target.variables;
        for (const varId in variables) {
            const variable = variables[varId];
            if (variable.name === name) {
                variable.value = value;
                return;
            }
        }

        throw new Error(`Variable "${name}" not found on "${spriteName || 'Stage'}"`);
    }

    /**
     * 実行制御コマンド
     */
    executeCommand (command) {
        const vm = this._getVM();
        if (!vm) throw new Error('VM not ready');

        switch (command) {
        case 'run':
            vm.greenFlag();
            break;
        case 'stop':
            vm.stopAll();
            break;
        case 'turbo_on':
            vm.setTurboMode(true);
            break;
        case 'turbo_off':
            vm.setTurboMode(false);
            break;
        default:
            throw new Error(`Unknown command: "${command}"`);
        }
    }

    // ===== 内部ヘルパー =====

    _getVM () {
        return this.vm || window.vm;
    }

    /**
     * VMから現在のプロジェクトJSONを取得（バックアップ用）
     */
    _getProjectJSON (vm) {
        let projectJSON = vm.toJSON();
        if (typeof projectJSON === 'string') {
            projectJSON = JSON.parse(projectJSON);
        }
        return projectJSON;
    }

    /**
     * バックアップからプロジェクトを復元
     */
    async _restoreBackup (vm, backup) {
        if (!backup) return;
        try {
            await vm.loadProject(backup);
            // eslint-disable-next-line no-console
            console.warn('[WorkspaceHandler] Restored project from backup after error');
        } catch (restoreError) {
            // eslint-disable-next-line no-console
            console.error('[WorkspaceHandler] Failed to restore backup:', restoreError);
        }
    }

    /**
     * コスチュームの検証と自動補完
     * 無効なコスチュームや空配列の場合、既存スプライトからコピーする
     */
    _ensureValidCostumes (costumes, ir, vm) {
        // 有効なコスチュームがあるかチェック
        if (costumes && costumes.length > 0) {
            const validCostumes = costumes.filter(c =>
                c && c.assetId && c.md5ext && c.format && c.name
            );

            if (validCostumes.length > 0) {
                // VMのストレージにアセットが存在するか確認
                const storage = vm.runtime && vm.runtime.storage;
                if (storage) {
                    const verified = validCostumes.filter(c => {
                        try {
                            // scratch-storageのキャッシュ確認
                            const asset = storage.get(c.assetId);
                            return !!asset;
                        } catch {
                            return false;
                        }
                    });
                    if (verified.length > 0) {
                        return verified;
                    }
                } else {
                    // ストレージチェック不可の場合はそのまま返す
                    return validCostumes;
                }
            }
        }

        // 既存スプライトからコスチュームをコピー
        return this._getDefaultCostume(ir, vm);
    }

    /**
     * 既存スプライト or ステージからコスチュームを1つ取得
     */
    _getDefaultCostume (ir, vm) {
        // 1. 既存スプライトの最初のコスチュームをコピー
        for (const sprite of ir.sprites) {
            if (sprite.costumes && sprite.costumes.length > 0) {
                const src = sprite.costumes[0];
                return [{
                    name: 'costume1',
                    assetId: src.assetId,
                    format: src.format,
                    md5ext: src.md5ext,
                    rotationCenter: {...src.rotationCenter},
                    bitmapResolution: src.bitmapResolution
                }];
            }
        }

        // 2. ステージのコスチューム（背景）をフォールバックとして使う
        if (ir.stage.costumes && ir.stage.costumes.length > 0) {
            const src = ir.stage.costumes[0];
            return [{
                name: 'costume1',
                assetId: src.assetId,
                format: src.format,
                md5ext: src.md5ext,
                rotationCenter: {...src.rotationCenter},
                bitmapResolution: src.bitmapResolution
            }];
        }

        // 3. VMのターゲットから直接取得（IRが空の場合のフォールバック）
        for (const target of vm.runtime.targets) {
            if (target.sprite && target.sprite.costumes && target.sprite.costumes.length > 0) {
                const c = target.sprite.costumes[0];
                return [{
                    name: 'costume1',
                    assetId: c.assetId,
                    format: c.dataFormat,
                    md5ext: c.md5ext || `${c.assetId}.${c.dataFormat}`,
                    rotationCenter: {
                        x: c.rotationCenterX || 0,
                        y: c.rotationCenterY || 0
                    },
                    bitmapResolution: c.bitmapResolution
                }];
            }
        }

        throw new Error('No existing costumes found. Cannot create sprite without a valid costume.');
    }
}

const workspaceHandler = new WorkspaceHandler();
export default workspaceHandler;
